import { Link, Navigate, useParams } from "react-router-dom";
import { CATEGORY_LABELS, getRelatedServices, getServiceBySlug } from "@/config/services";
import { waMessages } from "@/lib/whatsapp";
import { useSeo } from "@/hooks/useSeo";
import MediaImage from "@/components/feature/MediaImage";
import WhatsAppLink from "@/components/feature/WhatsAppLink";
import ServiceCard from "@/components/feature/ServiceCard";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = slug ? getServiceBySlug(slug) : undefined;

  useSeo({
    title: service
      ? `${service.name} | gülaYonar Bursa Nilüfer`
      : "gülaYonar | Bursa Nilüfer Saç ve Güzellik Salonu",
    description: service
      ? `${service.name} — ${service.intro} Bursa Nilüfer Odunluk'taki gülaYonar salonunda bilgi ve randevu için WhatsApp'tan ulaşın.`
      : undefined,
    path: `/hizmetler/${slug || ""}`,
  });

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const related = getRelatedServices(service);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <nav aria-label="breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <li>
            <Link to="/" className="transition-colors hover:text-accent-600">
              Ana Sayfa
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line"></i>
          </li>
          <li>
            <Link to="/#hizmetler" className="transition-colors hover:text-accent-600">
              Hizmetler
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line"></i>
          </li>
          <li aria-current="page" className="font-medium text-primary-800">
            {service.name}
          </li>
        </ol>
      </nav>

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
        <div className="flex flex-col justify-center">
          <span className="text-xs uppercase tracking-[0.28em] text-accent-600 md:text-sm">
            {CATEGORY_LABELS[service.category]}
          </span>
          <h1 className="mt-4 font-heading text-4xl leading-tight text-primary-800 md:text-6xl">
            {service.name}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-foreground-700 md:text-lg">
            {service.intro}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <WhatsAppLink
              message={waMessages.serviceInfo(service.name)}
              label="Bu İşlem İçin Bilgi Al"
              variant="primary"
              size="lg"
              sourceSection="service_detail_info"
              serviceId={service.id}
            />
            <WhatsAppLink
              message={waMessages.serviceAppointment(service.name)}
              label="Bu İşlem İçin Randevu Talebi"
              variant="whatsapp"
              size="lg"
              sourceSection="service_detail_appointment"
              serviceId={service.id}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-background-200">
          <div className="aspect-[4/5] w-full">
            <MediaImage src={service.image} alt={service.alt} eager className="h-full w-full" />
          </div>
        </div>
      </div>

      <section className="mt-16 md:mt-24">
        <h2 className="font-heading text-3xl leading-tight text-primary-800 md:text-4xl">
          Birlikte neleri konuşabiliriz?
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {service.talkingPoints.map((tp, i) => (
            <div
              key={tp.title}
              className="rounded-lg border border-background-300/60 bg-background-50 p-6"
            >
              <span className="font-heading text-3xl text-accent-500">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 font-heading text-xl text-primary-800">{tp.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{tp.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 md:mt-24">
        <h2 className="font-heading text-3xl leading-tight text-primary-800 md:text-4xl">
          İlgili hizmetler
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </div>
      </section>
    </div>
  );
}